// Sons de la MINE premium (Mine fun, lot B) : synthèse Web Audio à la volée, zéro asset — le
// pattern d'audio.ts, dans un module SÉPARÉ avec son PROPRE AudioContext (audio.ts est le
// territoire d'une autre session : on n'y touche pas, on IMPORTE seulement isMuted() pour
// respecter le réglage muet global). Le contexte se débloque sur les gestes déjà captés par la
// Mine (clavier / pointer) via mineAudioUnlock(). Tout est court, sec et borné ; le roulement
// (rumble) est le seul son CONTINU : nodes persistants, coupés par rumbleStop()/teardown.
//
// La dramaturgie (QUAND jouer quoi) vit dans le contrôleur (mine.ts) sur les horloges pures de
// mine-suspense : tick à chaque CHANGEMENT de palier, roulement dont l'intensité suit la lueur,
// SILENCE ~140 ms avant l'éclatement (le vide avant le boom), boom à l'éclatement, ping de
// pièce qui monte en pitch avec le combo (pluie d'or, lot C).
import { isMuted } from './audio'
import type { Rarity } from './mine-crack'

let ctx: AudioContext | null = null
function ac(): AudioContext | null {
  if (ctx) return ctx
  if (typeof AudioContext === 'undefined') return null
  ctx = new AudioContext()
  return ctx
}

/** À appeler sur un geste utilisateur (les navigateurs interdisent le son avant) : idempotent. */
export function mineAudioUnlock(): void {
  const c = ac()
  if (c && c.state === 'suspended') void c.resume()
}

// --- roulement de tambour (continu, intensité pilotée) ----------------------------------------
let rumbleSrc: AudioBufferSourceNode | null = null
let rumbleFilter: BiquadFilterNode | null = null
let rumbleGain: GainNode | null = null
let noiseBuf: AudioBuffer | null = null

function noise(c: AudioContext): AudioBuffer {
  if (noiseBuf) return noiseBuf
  const len = c.sampleRate // 1 s de bruit, loopé
  noiseBuf = c.createBuffer(1, len, c.sampleRate)
  const d = noiseBuf.getChannelData(0)
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
  return noiseBuf
}

let rumbleBase = 110 // fréquence de coupure de base : plus GRAVE en profondeur (l'étage 3 gronde)

export function rumbleStart(baseFreq = 110): void {
  const c = ac()
  rumbleBase = baseFreq
  if (!c || rumbleSrc || isMuted()) return
  rumbleSrc = c.createBufferSource()
  rumbleSrc.buffer = noise(c)
  rumbleSrc.loop = true
  rumbleFilter = c.createBiquadFilter()
  rumbleFilter.type = 'lowpass'
  rumbleFilter.frequency.value = rumbleBase
  rumbleFilter.Q.value = 0.8
  rumbleGain = c.createGain()
  rumbleGain.gain.value = 0
  rumbleSrc.connect(rumbleFilter)
  rumbleFilter.connect(rumbleGain)
  rumbleGain.connect(c.destination)
  rumbleSrc.start()
}

/** Intensité 0..1 (typiquement l'intensité de la lueur) : gronde de plus en plus fort et clair. */
export function rumbleSet(intensity: number): void {
  const c = ctx
  if (!c || !rumbleGain || !rumbleFilter) return
  const k = Math.max(0, Math.min(1, intensity))
  const mute = isMuted() ? 0 : 1
  rumbleGain.gain.setTargetAtTime((0.015 + 0.14 * k) * mute, c.currentTime, 0.08)
  rumbleFilter.frequency.setTargetAtTime(rumbleBase + 720 * k, c.currentTime, 0.1)
}

export function rumbleStop(): void {
  const c = ctx
  if (!c || !rumbleSrc || !rumbleGain) { rumbleSrc = null; rumbleFilter = null; rumbleGain = null; return }
  const src = rumbleSrc, gain = rumbleGain
  gain.gain.setTargetAtTime(0, c.currentTime, 0.03) // fondu express : le SILENCE dramatique
  src.stop(c.currentTime + 0.12)
  rumbleSrc = null
  rumbleFilter = null
  rumbleGain = null
}

// --- one-shots --------------------------------------------------------------------------------
/** Blip d'un oscillateur : forme, fréquence (avec glissando optionnel), gain, durée. */
function blip(type: OscillatorType, f0: number, f1: number, gain: number, durSec: number, delaySec = 0): void {
  const c = ac()
  if (!c || isMuted()) return
  const t0 = c.currentTime + delaySec
  const o = c.createOscillator()
  o.type = type
  o.frequency.setValueAtTime(f0, t0)
  if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + durSec)
  const g = c.createGain()
  g.gain.setValueAtTime(0, t0)
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec)
  o.connect(g)
  g.connect(c.destination)
  o.start(t0)
  o.stop(t0 + durSec + 0.02)
}

/** Souffle de bruit filtré : l'impact / le crack. */
function noiseBurst(freq: number, gain: number, durSec: number, delaySec = 0): void {
  const c = ac()
  if (!c || isMuted()) return
  const t0 = c.currentTime + delaySec
  const src = c.createBufferSource()
  src.buffer = noise(c)
  const f = c.createBiquadFilter()
  f.type = 'lowpass'
  f.frequency.value = freq
  const g = c.createGain()
  g.gain.setValueAtTime(gain, t0)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec)
  src.connect(f)
  f.connect(g)
  g.connect(c.destination)
  src.start(t0)
  src.stop(t0 + durSec + 0.02)
}

/** Tick de CHANGEMENT de palier (0 blanc, 1 bleu, 2 violet, 3 or) : le pitch monte — l'oreille
 *  apprend la hiérarchie avant même de lire la couleur. */
export function palierTick(n: number): void {
  const freqs = [400, 560, 760, 1020]
  const f = freqs[Math.max(0, Math.min(3, n))]!
  blip('triangle', f, f * 1.18, 0.14, 0.1)
  if (n >= 2) blip('sine', f * 2, f * 2, 0.05, 0.16, 0.02) // violet/or : harmonique brillante
}

/** Craquement de pierre à un STADE de fissures (séquence v2) : souffle sourd + fibre qui cède,
 *  de plus en plus GRAVE au fil des stades (le sol se disloque en profondeur). */
export function mineCreak(stage: number): void {
  noiseBurst(900 - stage * 180, 0.16, 0.14, 0)
  blip('triangle', 180 - stage * 25, 60, 0.1, 0.16, 0.02)
}

/** Aspiration d'IMPLOSION (les ~400 ms avant le boom) : lame qui MONTE + souffle aspiré. */
export function implosionWhoosh(): void {
  blip('sine', 180, 1400, 0.12, 0.38)
  noiseBurst(4200, 0.1, 0.35, 0.02)
}

/** L'ÉCLATEMENT : kick grave + crack de pierre, ampleur par rareté. (La fanfare de la carte
 *  reste celle d'audio.ts, plus tard dans la révélation.) */
export function burstBoom(rarity: Rarity): void {
  const big = rarity === 'epique' || rarity === 'mega'
  blip('sine', big ? 150 : 120, 38, big ? 0.5 : 0.34, big ? 0.34 : 0.24) // le coup dans le ventre
  noiseBurst(big ? 2600 : 1700, big ? 0.3 : 0.2, big ? 0.3 : 0.2, 0.005) // la pierre qui vole
  if (rarity === 'mega') blip('sawtooth', 300, 900, 0.08, 0.4, 0.05) // méga : lame ascendante
}

/** Ping de pièce ramassée (pluie d'or) : monte d'un demi-ton par combo — l'arcade pure. */
export function coinPing(combo: number): void {
  const f = 660 * Math.pow(2, Math.min(24, combo) / 12)
  blip('triangle', f, f, 0.1, 0.09)
  blip('sine', f * 1.5, f * 1.5, 0.04, 0.07, 0.01)
}

/** Coupe tout son continu (démontage de la scène). */
export function mineAudioTeardown(): void {
  rumbleStop()
}
